import EpirocMainPageHandler from 'handlers/epiroc.mainpage.handler';
import React, { useEffect, useState, useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import ReactApexChart from 'react-apexcharts';
import { type ApexOptions } from 'apexcharts';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import dayjs from 'dayjs';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { isDarkTheme } from 'utils/theme';

interface EventsLineChartProps {
  assetBase: string;
  location: string;
  startDate: string;
  endDate: string;
  height?: number | string;
  insightsContainerHeight?: number | string;
}

const EventsLineChart = ({
  assetBase,
  location,
  startDate,
  endDate,
  height = 650,
  insightsContainerHeight = 460,
}: EventsLineChartProps) => {
  const epirocMainPageHandler = new EpirocMainPageHandler();
  const { accessToken } = useAppSelector((state) => state.authReducer);
  const { theme } = useAppSelector((state) => state.authReducer?.customer);
  const enableDarkTheme = isDarkTheme(theme);

  type metric_name =
    | 'batteryaccumulatedenergy'
    | 'batteryenergydrained'
    | 'batterychargestatus'
    | 'drillmeter1'
    | 'drillmeter2'
    | 'boltcount'
    | 'totalloadedbuckets';

  interface EventInsight {
    metric_name: metric_name;
    metric_value: number;
    ts: string;
    uom: string;
  }

  const [eventsInsights, setEventsInsights] = useState<EventInsight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Friendly names for metrics
  const metricNames: Record<metric_name, string> = {
    batteryaccumulatedenergy: 'Battery Accumulated Energy',
    batteryenergydrained: 'Battery Energy Drained',
    batterychargestatus: 'Battery Charge Status',
    drillmeter1: 'Drill Meter 1',
    drillmeter2: 'Drill Meter 2',
    boltcount: 'Bolt Count',
    totalloadedbuckets: 'Total Loaded Buckets',
  };

  // Colors for each metric
  const metricColors: Record<metric_name, string> = {
    batteryaccumulatedenergy: '#3F51B5', // indigo
    batteryenergydrained: '#F44336', // red
    batterychargestatus: '#4CAF50', // green
    drillmeter1: '#FF9800', // orange
    drillmeter2: '#9C27B0', // purple
    boltcount: '#2196F3', // blue
    totalloadedbuckets: '#FFC107', // amber
  };

  const fetchEventsInsightsWrtAssetLocationTimestamps = async () => {
    if (!assetBase || !location || !startDate || !endDate) return;

    try {
      setLoading(true);
      setError(null);

      const cacheKey = `events_insights_${assetBase}_${location}_${startDate}_${endDate}`;
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData) {
        setEventsInsights(JSON.parse(cachedData));
      } else {
        const eventsInsights =
          await epirocMainPageHandler.getEventsInsightsWrtAssetLocationTimestamps(
            {
              startDate,
              endDate,
              assetBase,
              location,
            }
          );

        if (eventsInsights && Array.isArray(eventsInsights)) {
          sessionStorage.setItem(cacheKey, JSON.stringify(eventsInsights));
          setEventsInsights(eventsInsights);
        } else {
          setError('No data available for the selected parameters');
          setEventsInsights([]);
        }
      }
    } catch (err) {
      console.error('Error fetching event insights:', err);
      setError('Failed to fetch data. Please try again.');
      setEventsInsights([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    if (!startDate || !endDate) return;
    if (!assetBase || !location) return;
    void fetchEventsInsightsWrtAssetLocationTimestamps();
  }, [accessToken, startDate, endDate, assetBase, location]);

  // Group data by metric name
  const groupedData = useMemo(() => {
    const result: Record<string, Array<{ x: number; y: number }>> = {};

    eventsInsights.forEach((event) => {
      if (!result[event.metric_name]) {
        result[event.metric_name] = [];
      }

      result[event.metric_name].push({
        x: new Date(event.ts).getTime(),
        y: event.metric_value,
      });
    });

    // Sort each series by timestamp
    Object.keys(result).forEach((key) => {
      result[key].sort((a, b) => a.x - b.x);
    });

    return result;
  }, [eventsInsights]);

  // Prepare chart series
  const chartSeries = useMemo(() => {
    return Object.keys(groupedData).map((metricName) => {
      // find unit for this metric
      const unit = eventsInsights.find(
        (e) => e.metric_name === metricName
      )?.uom;
      const label = metricNames[metricName as metric_name] || metricName;
      const name = unit ? `${label} (${unit})` : label;
      return {
        metricKey: metricName,
        name,
        data: groupedData[metricName],
        color: metricColors[metricName as metric_name] || '#999',
      };
    });
  }, [groupedData, eventsInsights]);

  // Define metric groups for separate sections
  const activityMetricKeys: metric_name[] = [
    'drillmeter1',
    'drillmeter2',
    'boltcount',
    'totalloadedbuckets',
  ];
  const batteryMetricKeys: metric_name[] = [
    'batteryaccumulatedenergy',
    'batteryenergydrained',
    'batterychargestatus',
  ];
  const activitySeries = useMemo(
    () =>
      chartSeries.filter(
        (s) =>
          (s as any).metricKey &&
          activityMetricKeys.includes((s as any).metricKey as metric_name)
      ),
    [chartSeries]
  );
  const batterySeries = useMemo(
    () =>
      chartSeries.filter(
        (s) =>
          (s as any).metricKey &&
          batteryMetricKeys.includes((s as any).metricKey as metric_name)
      ),
    [chartSeries]
  );

  // Chart options
  const chartOptions = useMemo<ApexOptions>(() => {
    return {
      chart: {
        type: 'area',
        height: 350,
        zoom: {
          allowMouseWheelZoom: false,
          enabled: true,
        },
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: true,
          },
        },
        fontFamily: 'Inter, sans-serif',
        background: 'transparent',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100],
        },
      },
      grid: {
        borderColor: enableDarkTheme ? '#2d3748' : '#e2e8f0',
        strokeDashArray: 3,
        position: 'back',
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
          style: {
            colors: enableDarkTheme ? '#cbd5e0' : '#4a5568',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: enableDarkTheme ? '#cbd5e0' : '#4a5568',
          },
          formatter: (value) => value.toFixed(0),
        },
      },
      tooltip: {
        x: {
          format: 'dd MMM yyyy HH:mm:ss',
        },
        theme: enableDarkTheme ? 'dark' : 'light',
        fillSeriesColor: true,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        labels: {
          colors: enableDarkTheme ? '#fff' : '#000',
        },
      },
      theme: {
        mode: enableDarkTheme ? 'dark' : 'light',
      },
    };
  }, [enableDarkTheme]);

  // Format dates for display
  const formattedStartDate = dayjs(startDate).format('MMM D, YYYY HH:mm:ss');
  const formattedEndDate = dayjs(endDate).format('MMM D, YYYY HH:mm:ss');

  if (!assetBase || !location) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: enableDarkTheme
            ? ThemePalette.dark.contentBackground
            : ThemePalette.light.contentBackground,
          mb: 3,
        }}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Click on an event from the timeline chart to view detailed metrics
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: enableDarkTheme
          ? ThemePalette.dark.contentBackground
          : ThemePalette.light.contentBackground,
        mb: 3,
        overflow: 'hidden',
        maxHeight: `${height}px`,
        height: `${height}px`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: enableDarkTheme ? '#fff' : '#000',
          }}
        >
          Event & Battery Insights
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          <Chip
            label={`Asset: ${assetBase}`}
            color={enableDarkTheme ? 'default' : 'primary'}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              color: enableDarkTheme ? '#fff' : 'primary',
            }}
          />
          <Chip
            label={`Location: ${location}`}
            color="secondary"
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          />
        </Box>

        <Typography variant="body2" color={enableDarkTheme ? '#fff' : '#000'}>
          Time range: {formattedStartDate} to {formattedEndDate}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: `${insightsContainerHeight}px`,
            backgroundColor: enableDarkTheme
              ? `rgba(23, 42, 70, 0.7)`
              : `#80808080`,
          }}
        >
          <SingleFadeLoader isFullScreen={false} />
        </Box>
      ) : error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : !activitySeries.length && !batterySeries.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No insights available for the selected event
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxHeight: `${insightsContainerHeight}px`,
            overflow: 'auto',
          }}
        >
          {activitySeries.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  color: enableDarkTheme ? '#fff' : '#000',
                  fontWeight: 600,
                  mb: 1,
                  pb: 0.5,
                  borderBottom: `2px solid ${
                    enableDarkTheme ? '#fff' : '#000'
                  }`,
                }}
              >
                Activity Event Metrics
              </Typography>
              {activitySeries.map((series) => {
                const metricOptions: ApexOptions = {
                  ...chartOptions,
                  title: {
                    text: series.name,
                    align: 'left',
                    style: {
                      fontSize: '16px',
                      fontWeight: 600,
                      color: enableDarkTheme ? '#fff' : '#333',
                    },
                  },
                  colors: [series.color],
                };

                return (
                  <Paper
                    key={series.name}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: enableDarkTheme
                        ? 'rgba(23, 42, 70, 0.7)'
                        : 'rgba(255, 255, 255, 0.7)',
                      border: `1px solid ${
                        enableDarkTheme
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.1)'
                      }`,
                      height: 300,
                    }}
                  >
                    <ReactApexChart
                      options={metricOptions}
                      series={[series]}
                      type="area"
                      height="100%"
                    />
                  </Paper>
                );
              })}
            </>
          )}
          {batterySeries.length > 0 && (
            <>
              <Divider
                sx={{ my: 2, borderColor: enableDarkTheme ? '#555' : '#ccc' }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  color: enableDarkTheme ? '#fff' : '#000',
                  fontWeight: 600,
                  mb: 1,
                  pb: 0.5,
                  borderBottom: `2px solid ${
                    enableDarkTheme ? '#fff' : '#000'
                  }`,
                }}
              >
                Battery Insights Metrics
              </Typography>
              {batterySeries.map((series) => {
                const metricOptions: ApexOptions = {
                  ...chartOptions,
                  title: {
                    text: series.name,
                    align: 'left',
                    style: {
                      fontSize: '16px',
                      fontWeight: 600,
                      color: enableDarkTheme ? '#fff' : '#333',
                    },
                  },
                  colors: [series.color],
                };

                return (
                  <Paper
                    key={series.name}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: enableDarkTheme
                        ? 'rgba(23, 42, 70, 0.7)'
                        : 'rgba(255, 255, 255, 0.7)',
                      border: `1px solid ${
                        enableDarkTheme
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.1)'
                      }`,
                      height: 300,
                    }}
                  >
                    <ReactApexChart
                      options={metricOptions}
                      series={[series]}
                      type="area"
                      height="100%"
                    />
                  </Paper>
                );
              })}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default EventsLineChart;
